import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Search } from "lucide-react";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { supabase } from "@/integrations/supabase/client";
import backgroundNoise from "@/assets/background-noise.svg";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  author_name: string;
  read_time: string;
  hero_image: string;
  created_at: string;
}

const Blog = () => {
  const heroReveal = useScrollReveal();
  const postsReveal = useScrollReveal();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ["All", ...Array.from(new Set(posts.map(post => post.category)))];

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Structured data for blog listing page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Northquest Marketing Blog",
    "description": "Expert insights, trends, and practical strategies for digital marketing success in 2025",
    "url": "https://northquestmarketing.co.uk/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Northquest Marketing",
      "logo": {
        "@type": "ImageObject",
        "url": "https://northquestmarketing.co.uk/logo.svg"
      }
    },
    "blogPost": posts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": `https://northquestmarketing.co.uk${post.hero_image}`,
      "datePublished": post.created_at,
      "author": {
        "@type": "Organization",
        "name": post.author_name
      },
      "url": `https://northquestmarketing.co.uk/blog/${post.slug}`
    }))
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Digital Marketing Blog & Insights | Northquest Marketing</title>
        <meta name="title" content="Digital Marketing Blog & Insights | Northquest Marketing" />
        <meta name="description" content="Stay ahead with expert insights, trends, and practical strategies for digital marketing success in 2025. Read our latest articles on AI, SEO, social media, and more." />
        <meta name="keywords" content="digital marketing blog, marketing insights, SEO tips, social media marketing, AI marketing, influencer marketing, small business marketing" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://northquestmarketing.co.uk/blog" />
        <meta property="og:title" content="Digital Marketing Blog & Insights | Northquest Marketing" />
        <meta property="og:description" content="Stay ahead with expert insights, trends, and practical strategies for digital marketing success in 2025." />
        <meta property="og:image" content="https://northquestmarketing.co.uk/blog/ai-marketing-hero.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://northquestmarketing.co.uk/blog" />
        <meta property="twitter:title" content="Digital Marketing Blog & Insights | Northquest Marketing" />
        <meta property="twitter:description" content="Stay ahead with expert insights, trends, and practical strategies for digital marketing success in 2025." />
        <meta property="twitter:image" content="https://northquestmarketing.co.uk/blog/ai-marketing-hero.jpg" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://northquestmarketing.co.uk/blog" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-4 pt-24 md:pt-32 pb-12 md:pb-20 bg-foreground text-background overflow-hidden"
        style={{
          backgroundImage: `url(${backgroundNoise})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div 
          ref={heroReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            heroReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Marketing Insights & <span className="text-accent italic">Strategies</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-background/80 leading-relaxed max-w-3xl mx-auto mb-8">
              Stay ahead with expert insights, trends, and practical strategies for digital marketing success in 2025
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-14 text-base bg-background/95 backdrop-blur"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 border-b bg-muted/20">
        <div className="container mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-12 md:py-20 px-4">
        <div 
          ref={postsReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            postsReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {loading ? (
            <div className="text-center py-20">
              <p className="text-xl text-muted-foreground">Loading posts...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-muted-foreground">No articles found matching your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredPosts.map((post) => (
                <Link 
                  key={post.id} 
                  to={`/blog/${post.slug}`}
                  className="group"
                >
                  <div className="h-full flex flex-col">
                    <div className="relative h-48 md:h-56 overflow-hidden rounded-3xl mb-4">
                      <img 
                        src={post.hero_image} 
                        alt={post.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-accent text-accent-foreground rounded-full text-xs font-semibold">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col flex-grow">
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          <span>{new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          <span>{post.read_time}</span>
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-bold mb-3 line-clamp-2">
                        {post.title}
                      </h3>
                      
                      <p className="text-muted-foreground text-sm mb-4 flex-grow line-clamp-3">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center text-accent font-semibold text-sm">
                        Read More <ArrowRight size={16} className="ml-1" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
